import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import React, { Fragment, useMemo } from 'react';

import { Label, Listbox, ListboxOptions, Transition } from '@headlessui/react';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils';

import FormFieldMessage from '@/components/base/FormFieldMessage';
import Icon from '@/components/base/Icon';

import DefaultOption from './DefaultOption';

const selectPlacement = {
    bottom: 'mt-1',
    top: 'mb-1 bottom-full'
} as const;

const optionWrapperSizeStyle = {
    xs: 'text-xs',
    default: 'text-sm'
} as const;

export type OptionType = {
    value?: string | number | boolean;
    label?: string;
    disabled?: boolean;
};

export type SelectProps = ComponentPropsWithoutRef<'div'> & {
    selected: OptionType | OptionType[];
    setSelected: (option: OptionType | OptionType[]) => void;
    placement?: keyof typeof selectPlacement;
    label?: string;
    disabled?: boolean;
    required?: boolean;
    error?: string;
    className?: string;
    hasFilter?: boolean;
    options?: OptionType[];
    noBorder?: boolean;
    buttonBaseStyle?: keyof typeof selectButtonStyle;
    sizeStyle?: keyof typeof selectButtonSize;
    button?: ReactNode;
    children?: ReactNode;
    forcedDisplay?: boolean;
    noValue?: boolean;
    isTruncate?: boolean;
    isMultiple?: boolean;
    optionsWrapperClassName?: string;
};

/**
 * @deprecated Use SelectComponent instead
 */
const Select = ({
    selected,
    setSelected,
    placement,
    label,
    disabled = false,
    required = false,
    error,
    className = '',
    hasFilter = false,
    options = [],
    noBorder = false,
    buttonBaseStyle = 'default',
    sizeStyle = 'default',
    button,
    children,
    forcedDisplay = false,
    noValue = false,
    isTruncate = true,
    isMultiple = false,
    optionsWrapperClassName = '',
    ...restProps
}: SelectProps) => {
    const { t } = useTranslation('commons');

    return (
        <div className={classNames('w-full', className)} {...restProps}>
            <Listbox value={selected} onChange={setSelected} multiple={isMultiple}>
                {({ open }) => (
                    <div>
                        {label ? (
                            <Label className="mb-2 block text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                                {label}
                                {required ? <span className="text-icoBlue">*</span> : null}
                            </Label>
                        ) : null}
                        <div className="relative">
                            {button ? (
                                React.cloneElement(button as React.ReactElement<any>, {
                                    selected,
                                    disabled,
                                    error,
                                    noBorder
                                })
                            ) : (
                                <Select.ButtonDefault
                                    baseStyle={buttonBaseStyle}
                                    sizeStyle={sizeStyle}
                                    selected={selected}
                                    disabled={disabled}
                                    error={error}
                                    forcedDisplay={forcedDisplay}
                                    isMultiple={isMultiple}
                                />
                            )}
                            <Transition
                                show={open && !disabled}
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0">
                                <ListboxOptions
                                    static
                                    className={classNames(
                                        'scrollbar absolute z-20 max-h-60 overflow-auto rounded-md bg-white px-1 text-icoGray-500 shadow-lg focus:outline-none dark:bg-icoDarkMode-anthracite',
                                        selectPlacement[placement] ?? selectPlacement['bottom'],
                                        optionWrapperSizeStyle[sizeStyle] ??
                                            optionWrapperSizeStyle['default'],
                                        {
                                            'w-56': noValue,
                                            '!w-full': !noValue,
                                            'py-0 pb-1': hasFilter,
                                            'py-1': !hasFilter,
                                            'ring-1 ring-icoGray-300 dark:ring-icoDarkMode-beton':
                                                !noBorder
                                        },
                                        optionsWrapperClassName
                                    )}>
                                    {options ? (
                                        <>
                                            {!options.length ? (
                                                <div className="py-1 text-center">
                                                    {t('select_no_options')}
                                                </div>
                                            ) : null}
                                            {options?.map((option, id) => {
                                                const selectedOptions = selected;
                                                return (
                                                    <Select.Option
                                                        key={id}
                                                        sizeStyle={sizeStyle}
                                                        disabled={option.disabled ?? null}
                                                        value={option}>
                                                        {({ selected, disabled }) => (
                                                            <>
                                                                {children ? (
                                                                    React.Children.map(
                                                                        children,
                                                                        (child) => {
                                                                            if (
                                                                                typeof child[
                                                                                    'type'
                                                                                ] === 'string' ||
                                                                                !child['type']
                                                                            ) {
                                                                                return child;
                                                                            }
                                                                            const newChild =
                                                                                React.cloneElement(
                                                                                    child as React.ReactElement<any>,
                                                                                    {
                                                                                        selected:
                                                                                            isMultiple &&
                                                                                            Array.isArray(
                                                                                                selectedOptions
                                                                                            )
                                                                                                ? !!selectedOptions?.find(
                                                                                                      (
                                                                                                          el
                                                                                                      ) =>
                                                                                                          el?.value ===
                                                                                                          option?.value
                                                                                                  )
                                                                                                : selected,
                                                                                        disabled,
                                                                                        option
                                                                                    }
                                                                                );
                                                                            return newChild;
                                                                        }
                                                                    )
                                                                ) : (
                                                                    <DefaultOption
                                                                        selected={
                                                                            isMultiple &&
                                                                            Array.isArray(
                                                                                selectedOptions
                                                                            )
                                                                                ? !!selectedOptions?.find(
                                                                                      (el) =>
                                                                                          el?.value ===
                                                                                          option?.value
                                                                                  )
                                                                                : selected
                                                                        }
                                                                        disabled={disabled}
                                                                        option={option}
                                                                        isTruncate={isTruncate}
                                                                        forcedDisplay={
                                                                            forcedDisplay
                                                                        }
                                                                        isMultiple={isMultiple}
                                                                    />
                                                                )}
                                                            </>
                                                        )}
                                                    </Select.Option>
                                                );
                                            })}
                                        </>
                                    ) : (
                                        <>{children}</>
                                    )}
                                </ListboxOptions>
                            </Transition>
                        </div>
                    </div>
                )}
            </Listbox>
            {error && typeof error === 'string' && !disabled ? (
                <FormFieldMessage>{error}</FormFieldMessage>
            ) : null}
        </div>
    );
};
Select.displayName = Select;

const optionsSizeStyle = {
    xs: 'text-xs p-2',
    default: 'text-sm py-2 px-4'
} as const;

type OptionProps = Pick<SelectProps, 'sizeStyle' | 'disabled'> & {
    children: ({
        active,
        selected,
        disabled
    }: {
        selected: boolean;
        active: boolean;
        disabled: boolean;
    }) => React.ReactElement;
    value: OptionType;
};

const Option = ({ sizeStyle = 'default', children, value, ...restProps }: OptionProps) => (
    <Listbox.Option
        className={({ active, selected, disabled }) => {
            return classNames(
                'relative cursor-pointer select-none rounded-md outline-none duration-200',
                optionsSizeStyle[sizeStyle] ?? optionsSizeStyle['default'],
                {
                    'text-icoBlue': selected,
                    'bg-icoBlue-100 text-icoBlue dark:bg-icoBlue/30': active,
                    'cursor-default text-icoGray-400': disabled
                }
            );
        }}
        value={value}
        {...restProps}>
        {({ active, selected, disabled }) => children({ active, selected, disabled })}
    </Listbox.Option>
);

Select.Option = Option;

const selectDisabled =
    'bg-icoGray-300 dark:text-icoGray-400 dark:bg-icoDarkMode-goudron cursor-not-allowed';
const buttonBaseStyle =
    'relative font-GSemiBold w-full text-left rounded-md focus:outline-none focus:ring-offset-0 focus:ring-2 focus:ring-opacity-75';
const selectButtonStyle = {
    default:
        'border border-icoGray-300 dark:border-icoDarkMode-beton bg-white dark:bg-icoDarkMode-anthracite focus:border-icoBlue focus:ring-icoBlue-200',
    noBorder: 'bg-white dark:bg-icoDarkMode-anthracite focus:border-icoBlue focus:ring-icoBlue-200',
    grayNoBorder: 'bg-icoGray-100 dark:bg-icoDarkMode-deepBlack',
    grayNoBorderLighter: 'bg-icoGray-100 dark:bg-icoDarkMode-anthracite',
    whiteNoBorder: 'bg-white dark:bg-icoDarkMode-deepBlack',
    whiteWithBorder:
        'bg-white dark:bg-icoDarkMode-deepBlack border border-icoGray-300 dark:border-icoDarkMode-beton',
    grayBackground:
        'border border-icoGray-300 dark:border-icoDarkMode-beton bg-icoGray-100 py-[0.7rem] dark:bg-icoDarkMode-deepBlack focus:border-icoBlue focus:ring-icoBlue-200',
    transparentBackground: 'border-none bg-transparent text-xl',
    lightBorder: 'border border-icoGray-300 dark:border-icoDarkMode-deepBlack'
} as const;

const selectButtonSize = {
    xs: 'h-8 text-xs',
    sm: 'py-2 text-sm',
    default: 'rounded-md text-sm min-h-12',
    lg: 'rounded-md text-sm min-h-12',
    noValue: 'p-0',
    noPadding: 'rounded-md text-sm min-h-12 pl-1 pr-9'
} as const;

const selectButtonStyleError =
    'border border-icoRed bg-white dark:bg-icoDarkMode-anthracite focus:ring-icoRed-200';

export type ButtonProps = Pick<SelectProps, 'error' | 'disabled' | 'sizeStyle' | 'selected'> & {
    baseStyle?: keyof typeof selectButtonStyle;
    chevron?: boolean;
    children?: ReactNode;
    className?: string;
};

const Button = ({
    error,
    disabled,
    baseStyle = 'default',
    sizeStyle = 'default',
    chevron = true,
    selected,
    className,
    children
}: ButtonProps) => (
    <Listbox.Button
        data-testid="select-button"
        className={classNames(
            buttonBaseStyle,
            selectButtonSize[sizeStyle],
            {
                [selectButtonStyleError]: error && !disabled,
                [selectButtonStyle[baseStyle]]: !error && !disabled,
                [selectDisabled]: disabled,
                'px-5': !chevron && sizeStyle === 'default',
                'pl-5 pr-9':
                    chevron && sizeStyle === 'default' && baseStyle !== 'transparentBackground',
                'px-3': !chevron && (sizeStyle === 'xs' || sizeStyle === 'sm'),
                'pl-3 pr-7': chevron && (sizeStyle === 'xs' || sizeStyle === 'sm')
            },
            className
        )}>
        {React.Children.map(children, (child) => {
            if (typeof child['type'] === 'string' || !child['type']) {
                return child;
            }
            const newChild = React.cloneElement(child as React.ReactElement<any>, {
                selected
            });
            return newChild;
        })}
        {chevron ? (
            <span
                className={classNames(
                    'pointer-events-none absolute inset-y-0 right-0 flex items-center',
                    {
                        'pr-5': sizeStyle === 'default' || sizeStyle === 'noPadding',
                        'pr-3': sizeStyle === 'xs' || sizeStyle === 'sm',
                        'p-0': sizeStyle === 'noValue'
                    }
                )}>
                <Icon
                    icon="chevron-bottom"
                    className={classNames('text-icoGray-500', {
                        'text-xs': sizeStyle === 'noValue',
                        'text-icoGray-300 dark:text-icoDarkMode-beton': disabled
                    })}
                />
            </span>
        ) : null}
    </Listbox.Button>
);

Select.Button = Button;

type DefaultButtonProps = ButtonProps & {
    forcedDisplay;
    isMultiple;
};

const ButtonDefault = ({
    error,
    disabled,
    selected,
    baseStyle,
    sizeStyle,
    forcedDisplay,
    isMultiple
}: DefaultButtonProps) => {
    const labels = useMemo(() => {
        let result = '';
        if (isMultiple && Array.isArray(selected)) {
            let i = 0;
            for (const label of selected) {
                if (i > 0) {
                    result += ', ';
                }
                result += label?.label ?? label?.value;
                i++;
            }
        } else if (!isMultiple && !Array.isArray(selected)) {
            result = (selected?.label ?? selected?.value) as string;
        }
        return result;
    }, [isMultiple, selected]);

    return (
        <Select.Button
            selected={selected}
            disabled={disabled}
            error={error}
            baseStyle={baseStyle}
            sizeStyle={sizeStyle}>
            <span
                data-testid="selectedContent"
                className={classNames('block truncate', {
                    'text-icoGray-400 dark:text-icoDarkMode-beton':
                        (!Array.isArray(selected) && selected?.value === null && !forcedDisplay) ||
                        (Array.isArray(selected) &&
                            selected?.filter((el) => !el?.value)?.length === 0 &&
                            !forcedDisplay) ||
                        disabled,
                    'text-icoBlack dark:text-white':
                        (!Array.isArray(selected) &&
                            selected?.label &&
                            selected?.value &&
                            !error &&
                            !disabled) ||
                        (Array.isArray(selected) &&
                            selected.filter((el) => el?.label && el?.value)?.length ===
                                selected.length &&
                            !error &&
                            !disabled),
                    'text-icoRed': error
                })}>
                {labels}
            </span>
        </Select.Button>
    );
};

Select.ButtonDefault = ButtonDefault;

export default Select;
